import React, { useState, useEffect, useRef } from 'react';
import { Input, Button, Space, Modal, Tooltip } from 'antd';
import { EditOutlined, InfoCircleOutlined, MedicineBoxOutlined, ThunderboltOutlined } from '@ant-design/icons';
import './ToothChartGeneral.css';
import HtmlParser from 'react-html-parser';
import { _newLine } from '@/utils/helpers';
import { TOOTH_ACTIONS, toothNumbers } from './helpers';

const TOOTH_ACTION_MAP = TOOTH_ACTIONS.reduce((map, item) => {
    map[item.value] = item;
    return map;
}, {});

const getToothSrcPublic = (num) => `/tooth_images/${num}.png`;

const parseActions = (item) => {
    if (Array.isArray(item.actions)) return item.actions;

    if (item.actions_json) {
        try {
            const parsed = JSON.parse(item.actions_json);
            return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
            return [];
        }
    }

    // Tương thích dữ liệu cũ
    if (item.status && item.status !== 'none') {
        return [item.status];
    }

    return [];
};

const ToothChartGeneral = ({ onDataChange, data }) => {
    const [selected, setSelected] = useState([]);
    const [toothActions, setToothActions] = useState(
        Object.fromEntries(toothNumbers.map(n => [n, []]))
    );
    const [deletedTeeth, setDeletedTeeth] = useState([]);
    const [notes, setNotes] = useState(
        Object.fromEntries(toothNumbers.map(n => [n, '']))
    );
    const [noteModal, setNoteModal] = useState({
        open: false,
        num: null,
        value: '',
    });

    const isFirstLoad = useRef(true);

    useEffect(() => {
        if (data && Array.isArray(data)) {
            const newActions = Object.fromEntries(toothNumbers.map(n => [n, []]));
            const newDeleted = [];
            const newNotes = Object.fromEntries(toothNumbers.map(n => [n, '']));

            data.forEach(item => {
                const num = parseInt(item.tooth_number, 10);

                if (Number.isInteger(num) && toothNumbers.includes(num)) {
                    newActions[num] = parseActions(item);
                    newNotes[num] = item.note || '';

                    if (item.is_deleted) {
                        newDeleted.push(num);
                    }
                }
            });

            setToothActions(newActions);
            setDeletedTeeth(newDeleted);
            setNotes(newNotes);
            isFirstLoad.current = true;
        }
    }, [data]);

    useEffect(() => {
        if (isFirstLoad.current) {
            isFirstLoad.current = false;
            return;
        }

        if (onDataChange) {
            onDataChange(prepareDataForSave());
        }
    }, [toothActions, deletedTeeth, notes]);

    const prepareDataForSave = () => {
        return toothNumbers.map(num => ({
            tooth_number: num,
            actions: toothActions[num] || [],
            actions_json: JSON.stringify(toothActions[num] || []),

            // Giữ status để tương thích backend cũ
            status: toothActions[num]?.[0] || 'none',

            note: notes[num] || '',
            is_deleted: deletedTeeth.includes(num) ? 1 : 0,
        }));
    };

    const toggleSelect = (num) => {
        setSelected(prev =>
            prev.includes(num)
                ? prev.filter(n => n !== num)
                : [...prev, num]
        );
    };

    const applyAction = (actionValue) => {
        if (selected.length === 0) return;

        setToothActions(prev => {
            const next = { ...prev };

            selected.forEach(num => {
                const current = next[num] || [];

                next[num] = current.includes(actionValue)
                    ? current.filter(item => item !== actionValue)
                    : [...current, actionValue];
            });

            return next;
        });
    };

    const handleAction = (action) => {
        switch (action) {
            case 'delete':
                if (selected.length === 0) return;
                setDeletedTeeth(prev => Array.from(new Set([...prev, ...selected])));
                setSelected([]);
                break;

            case 'reset':
                if (selected.length === 0) return;

                setToothActions(prev => {
                    const next = { ...prev };
                    selected.forEach(num => {
                        next[num] = [];
                    });
                    return next;
                });

                setDeletedTeeth(prev => prev.filter(num => !selected.includes(num)));

                setNotes(prev => {
                    const next = { ...prev };
                    selected.forEach(num => {
                        next[num] = '';
                    });
                    return next;
                });

                setSelected([]);
                break;

            case 'selectAll':
                setSelected(toothNumbers);
                break;

            case 'deselect':
                setSelected([]);
                break;

            default:
                applyAction(action);
                break;
        }
    };

    const openNote = (num) => {
        setNoteModal({
            open: true,
            num,
            value: notes[num] || '',
        });
    };

    const saveNote = () => {
        const { num, value } = noteModal;

        if (num == null) return;

        setNotes(prev => ({
            ...prev,
            [num]: value,
        }));

        setNoteModal({
            open: false,
            num: null,
            value: '',
        });
    };

    const renderTooth = (num) => {
        const actions = toothActions[num] || [];
        const isShapeHidden = deletedTeeth.includes(num);
        const imgSrc = getToothSrcPublic(num);
        const note = notes[num];
        const hasActions = actions.length > 0;

        return (
            <Tooltip
                key={num}
                title={note ? HtmlParser(note) : null}
                placement="top"
                mouseEnterDelay={0.3}
            >
                <div
                    className={`tooth ${selected.includes(num) ? 'selected' : ''} ${isShapeHidden ? 'tooth-deleted' : ''}`}
                    onClick={() => toggleSelect(num)}
                >
                    {/* ===== Number + indicator ===== */}
                    <div className="tooth-number-wrapper">
                        <div className="tooth-number">{num}</div>

                        {note && (
                            <span
                                className="tooth-note-indicator"
                                onClick={(e) => e.stopPropagation()}
                            />
                        )}
                    </div>

                    {/* ===== Actions ===== */}
                    <div className="tooth-action-zone">
                        {hasActions ? (
                            <div className="tooth-action-badges">
                                {actions.map(actionValue => {
                                    const action = TOOTH_ACTION_MAP[actionValue];

                                    return (
                                        <div
                                            key={actionValue}
                                            className="tooth-action-badge"
                                            style={{
                                                background: action?.color || '#64748b',
                                            }}
                                        >
                                            {action?.label || actionValue}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="tooth-action-empty" />
                        )}
                    </div>

                    {/* ===== Image ===== */}
                    <div className="tooth-image-box">
                        {isShapeHidden ? (
                            <div className="tooth-image-placeholder" />
                        ) : (
                            <img
                                src={imgSrc}
                                alt={`Tooth ${num}`}
                                className="tooth-image"
                                draggable={false}
                            />
                        )}
                    </div>

                    {/* ===== Footer ===== */}
                    <div className="tooth-footer">
                        <Tooltip title={note ? 'Sửa ghi chú' : 'Thêm ghi chú'}>
                            <Button
                                size="small"
                                className={`note-btn ${note ? 'has-note' : ''}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    openNote(num);
                                }}
                            >
                                <EditOutlined />
                            </Button>
                        </Tooltip>
                    </div>
                </div>
            </Tooltip>
        );
    };

    return (
        <div className="tooth-chart-wrapper">
            <div className="tooth-medical-header">
                <div className="tooth-title-block">
                    <div className="tooth-title-icon">
                        <MedicineBoxOutlined />
                    </div>

                    <div>
                        <div className="tooth-medical-title">Sơ đồ răng tổng quát</div>
                        <div className="tooth-medical-desc">
                            Chọn một hoặc nhiều răng để gán tình trạng, chỉ định điều trị và ghi chú lâm sàng.
                        </div>
                    </div>
                </div>

                <div className="tooth-selected-card">
                    <div className="selected-label">Đang chọn</div>
                    <div className="selected-value">
                        {selected.length}
                        <span> răng</span>
                    </div>
                    <div className="selected-list">
                        {selected.length > 0 ? selected.join(', ') : 'Chưa chọn răng'}
                    </div>
                </div>
            </div>

            <div className="tooth-grid">
                <div className="tooth-row top-row">
                    {toothNumbers.slice(0, 16).map(num => renderTooth(num))}
                </div>

                <div className="tooth-row bottom-row">
                    {toothNumbers.slice(16).map(num => renderTooth(num))}
                </div>
            </div>

            <div className="tooth-medical-panel">
                <div className="tooth-toolbar-section quick-section">
                    <div className="toolbar-title">
                        <ThunderboltOutlined /> Thao tác nhanh
                    </div>

                    <Space wrap>
                        <Button onClick={() => handleAction('selectAll')}>
                            Chọn tất cả
                        </Button>

                        <Button onClick={() => handleAction('deselect')}>
                            Bỏ chọn
                        </Button>

                        <Button danger onClick={() => handleAction('delete')}>
                            Đánh dấu mất răng
                        </Button>

                        <Button onClick={() => handleAction('reset')}>
                            Reset răng
                        </Button>
                    </Space>
                </div>

                <div className="tooth-toolbar-section action-section">
                    <div className="toolbar-title">
                        <MedicineBoxOutlined /> Tình trạng / chỉ định điều trị
                    </div>

                    <Space wrap>
                        {TOOTH_ACTIONS.map(action => (
                            <Button
                                key={action.value}
                                className="tooth-action-button"
                                style={{
                                    background: action.color,
                                    borderColor: action.color,
                                }}
                                onClick={() => applyAction(action.value)}
                            >
                                {action.label}
                            </Button>
                        ))}
                    </Space>
                </div>

                <div className="tooth-help-box">
                    <InfoCircleOutlined />
                    <span>
                        Nhấn vào răng để chọn hoặc bỏ chọn.
                        Sau đó chọn tình trạng bên dưới để gán hoặc hủy cho các răng đã chọn.
                        Có thể reset từng răng hoặc reset toàn bộ về trạng thái ban đầu.
                    </span>
                </div>
            </div>

            <Modal
                open={noteModal.open}
                title={`Ghi chú cho răng ${noteModal.num}`}
                onCancel={() => setNoteModal({ open: false, num: null, value: '' })}
                onOk={saveNote}
                okText="Lưu"
                cancelText="Hủy"
            >
                <Input.TextArea
                    rows={10}
                    value={_newLine(noteModal.value)}
                    onChange={(e) =>
                        setNoteModal(prev => ({
                            ...prev,
                            value: e.target.value,
                        }))
                    }
                    placeholder="Nhập ghi chú lâm sàng cho răng này..."
                />
            </Modal>
        </div>
    );
};

export default ToothChartGeneral;